import React from 'react';
import { ArrowLeft, Scale, Shield, AlertCircle, FileText, UserCheck, Zap, DollarSign, Ban, Lock, Gavel, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PublicHeader } from '../PublicHeader';
import { Footer } from '../Footer';

export function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-gradient-to-br from-servet-dark-blue to-servet-blue rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <div className="flex items-center justify-center mb-6">
                <Scale className="h-12 w-12 text-servet-red" />
              </div>
              <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
              <p className="text-white/90">
                Last Updated: February 3, 2025
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  Welcome to <strong>BidWin by SerVet Digital LLC</strong> ("BidWin", "we", "our", or "us"). These <strong>Terms of Service</strong> ("Terms") govern your access and use of our <strong>BidWin</strong> platform, including all features, content, and services offered through <strong>bidwin.servetdigital.com</strong> (the "Platform").
                </p>
                <p className="text-gray-600">
                  By using BidWin, you ("User", "you", or "your") agree to these Terms. If you do not agree, <strong>do not use the Platform.</strong>
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <UserCheck className="h-6 w-6 mr-2 text-servet-blue" />
                2. Eligibility
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">To use BidWin, you must:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li>Be <strong>at least 18 years old</strong>.</li>
                  <li>Provide <strong>accurate and truthful</strong> information during sign-up.</li>
                  <li>Have the <strong>legal authority</strong> to enter into these Terms.</li>
                  <li>Not be prohibited from using the Platform under any applicable laws.</li>
                </ul>
              </div>
            </section>

            {/* Services */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="h-6 w-6 mr-2 text-servet-blue" />
                3. Services Provided
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  BidWin is a <strong>government contract bidding platform</strong> powered by AI. Our services include:
                </p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>Contract Opportunity Matching</strong>: AI-driven recommendations for federal, state, and local contracts.</li>
                  <li><strong>Proposal Generation & Optimization</strong>: AI-assisted proposal writing and scoring.</li>
                  <li><strong>Bid Tracking & Management</strong>: Tools to track, manage, and optimize your contract applications.</li>
                  <li><strong>Subscription Plans</strong>: Free and paid tiers with different levels of access to AI tokens and contract data.</li>
                </ul>
              </div>
            </section>

            {/* User Accounts */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 mr-2 text-servet-blue" />
                4. User Accounts & Responsibilities
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li>You must keep your <strong>account information accurate and updated</strong>.</li>
                  <li>You are responsible for <strong>all activity under your account</strong>.</li>
                  <li>You must <strong>not share your login credentials</strong> or allow unauthorized access.</li>
                  <li>We reserve the right to <strong>suspend or terminate accounts</strong> that violate these Terms.</li>
                </ul>
              </div>
            </section>

            {/* AI Content */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-6 w-6 mr-2 text-servet-blue" />
                5. AI-Generated Content
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>No Guarantees</strong>: AI-generated proposals and contract insights are <strong>for informational purposes only</strong> and <strong>do not guarantee contract awards</strong>.</li>
                  <li><strong>User Review Required</strong>: You are responsible for reviewing and modifying AI-generated content before submission.</li>
                  <li><strong>No Liability</strong>: We are <strong>not liable</strong> for inaccuracies or errors in AI-generated proposals.</li>
                </ul>
              </div>
            </section>

            {/* Payments */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-6 w-6 mr-2 text-servet-blue" />
                6. Payments & Subscription Plans
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">BidWin offers Free, Growth, Pro, and Enterprise plans.</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>Billing</strong>: Paid plans are <strong>billed monthly or annually</strong>.</li>
                  <li><strong>Auto-Renewal</strong>: Subscriptions auto-renew unless canceled.</li>
                  <li><strong>Refunds</strong>: We do <strong>not offer refunds</strong> once payment is processed.</li>
                  <li><strong>Plan Changes</strong>: You may <strong>upgrade or downgrade</strong> your plan at any time.</li>
                </ul>
              </div>
            </section>

            {/* Prohibited Activities */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Ban className="h-6 w-6 mr-2 text-servet-blue" />
                7. Prohibited Activities
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">Users <strong>must not</strong>:</p>
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>Misuse AI features</strong> for fraudulent contract applications.</li>
                  <li><strong>Use automated tools</strong> to extract contract data.</li>
                  <li><strong>Violate applicable federal, state, or local laws</strong>.</li>
                  <li><strong>Share or resell</strong> BidWin-generated content without permission.</li>
                </ul>
              </div>
            </section>

            {/* Data Privacy */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 mr-2 text-servet-blue" />
                8. Data Privacy & Security
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li>We collect, store, and process <strong>limited user data</strong> for service functionality.</li>
                  <li>We do <strong>not</strong> share your information with third parties without consent.</li>
                  <li>See our <strong>Privacy Policy</strong> for full details on data handling.</li>
                </ul>
              </div>
            </section>

            {/* Disclaimers */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Disclaimers & Limitation of Liability
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li><strong>No Warranties</strong>: BidWin <strong>does not guarantee</strong> contract success or AI accuracy.</li>
                  <li><strong>Service Availability</strong>: We may <strong>modify, suspend, or discontinue</strong> services at any time.</li>
                  <li><strong>Limited Liability</strong>: We are <strong>not liable</strong> for damages arising from your use of BidWin.</li>
                </ul>
              </div>
            </section>

            {/* Termination */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Termination of Services
              </h2>
              <div className="prose prose-blue max-w-none">
                <ul className="list-disc pl-6 text-gray-600">
                  <li>We may <strong>suspend or terminate</strong> your account for violating these Terms.</li>
                  <li>Users may <strong>cancel their account</strong> at any time.</li>
                </ul>
              </div>
            </section>

            {/* Governing Law */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Gavel className="h-6 w-6 mr-2 text-servet-blue" />
                11. Governing Law
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  These Terms are governed by the laws of <strong>North Carolina, USA</strong>.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 mr-2 text-servet-blue" />
                12. Contact Information
              </h2>
              <div className="prose prose-blue max-w-none">
                <p className="text-gray-600">
                  For questions or concerns, contact us at <strong>support@servetdigital.com</strong>.
                </p>
              </div>
            </section>

            {/* Agreement */}
            <section className="bg-white rounded-xl shadow-lg p-8">
              <p className="text-center text-gray-600 font-medium">
                By using BidWin, you acknowledge and agree to these Terms.
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}